import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import InventoryService from "../../../services/InventoryService";
import StockInventoryService from "../../../services/StockInventoryService";
import fidLogo from "../../../assets/images/fid_logo.png";

const FILTER_KEYS = [
  "name_materiel",
  "code_immo",
  "state",
  "logistics_manager",
  "status1",
  "status2",
];

const COMMON_COLUMNS = [
  { field: "code_immo", headerName: "Code Immo", width: 20 },
  { field: "name", headerName: "Matériel", width: 32 },
  { field: "state", headerName: "État matériel", width: 20 },
  { field: "date_inventaire", headerName: "Date inventaire", width: 20 },
  { field: "observation", headerName: "Observation", width: 35 },
  { field: "logistics_manager", headerName: "Resp. affectation", width: 30 },
];

function formatStatus(status) {
  const labels = {
    CONFIRMED: "Validé",
    PENDING: "En attente de validation",
    DECLINED: "Non validé",
  };

  return labels[status] || status || "";
}

const VALIDATION_COLUMNS = [
  ...COMMON_COLUMNS,
  { field: "new_affectation", headerName: "Nouveau détenteur", width: 30 },
  { field: "oldest_affectation", headerName: "Ancien détenteur", width: 30 },
  {
    field: "status_by_the_logistics_manager",
    headerName: "Validation Resp. Logistique",
    width: 30,
    formatter: formatStatus,
  },
  {
    field: "status_by_the_holder",
    headerName: "Statut détenteur",
    width: 25,
    formatter: formatStatus,
  },
  {
    field: "status_by_the_old_holder",
    headerName: "Statut ancien détenteur",
    width: 27,
    formatter: formatStatus,
  },
];

const TAB_CONFIG = {
  0: {
    title: "Validations des immobilisations",
    sheetName: "Validations",
    filePrefix: "validations_immobilisations",
    columns: VALIDATION_COLUMNS,
    loadRows: (...filters) => InventoryService.getMinePendingImmoHistory(...filters),
  },
  1: {
    title: "Validations des immobilisations en stock",
    sheetName: "Validations stock",
    filePrefix: "validations_immobilisations_stock",
    columns: COMMON_COLUMNS,
    loadRows: (...filters) => StockInventoryService.getMinePendingStockHistory(...filters),
  },
};

const BORDER = {
  top: { style: "thin", color: { argb: "FFB7B7B7" } },
  bottom: { style: "thin", color: { argb: "FFB7B7B7" } },
  left: { style: "thin", color: { argb: "FFB7B7B7" } },
  right: { style: "thin", color: { argb: "FFB7B7B7" } },
};

function getFilters() {
  return FILTER_KEYS.map((key) => localStorage.getItem(key) || "");
}

function getTabConfig(activeTab) {
  return TAB_CONFIG[Number(activeTab) === 1 ? 1 : 0];
}

async function addLogo(workbook, worksheet) {
  const response = await fetch(fidLogo);
  if (!response.ok) {
    throw new Error("Le logo FID n'a pas pu être chargé.");
  }

  const logoId = workbook.addImage({
    buffer: await response.arrayBuffer(),
    extension: "png",
  });

  worksheet.addImage(logoId, {
    tl: { col: 0.15, row: 0.15 },
    ext: { width: 120, height: 55 },
  });
}

async function buildWorkbook(config, rows) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "FID";
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet(config.sheetName, {
    views: [{ state: "frozen", ySplit: 4, showGridLines: false }],
  });

  config.columns.forEach((column, index) => {
    worksheet.getColumn(index + 1).width = column.width;
  });

  worksheet.getRow(1).height = 22;
  worksheet.getRow(2).height = 22;
  worksheet.getRow(3).height = 22;
  worksheet.mergeCells(1, 3, 3, config.columns.length);

  const titleCell = worksheet.getCell(1, 3);
  titleCell.value = config.title;
  titleCell.font = { bold: true, size: 18, color: { argb: "FF1F4E78" } };
  titleCell.alignment = { vertical: "middle", horizontal: "center" };

  await addLogo(workbook, worksheet);

  const headerRow = worksheet.getRow(4);
  headerRow.height = 28;
  headerRow.values = config.columns.map((column) => column.headerName);
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF4F81BD" },
    };
    cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    cell.border = BORDER;
  });

  rows.forEach((sourceRow) => {
    const values = config.columns.map((column) => {
      const value = sourceRow[column.field];
      return column.formatter ? column.formatter(value) : value ?? "";
    });
    const row = worksheet.addRow(values);
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.alignment = { vertical: "middle", wrapText: true };
      cell.border = BORDER;
    });
  });

  worksheet.autoFilter = {
    from: { row: 4, column: 1 },
    to: { row: 4, column: config.columns.length },
  };

  return workbook;
}

export async function exportImmobilisationValidationExcel(activeTab) {
  const config = getTabConfig(activeTab);
  const result = await config.loadRows(...getFilters());

  if (!result || !result.status) {
    throw new Error(result?.message || "Impossible de récupérer les immobilisations à exporter.");
  }

  const rows = Array.isArray(result.data) ? result.data : [];
  if (rows.length === 0) {
    return { exported: false, rowCount: 0 };
  }

  const workbook = await buildWorkbook(config, rows);
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const date = new Date().toISOString().slice(0, 10);

  saveAs(blob, `${config.filePrefix}_${date}.xlsx`);

  return { exported: true, rowCount: rows.length };
}